import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Dialog, Transition } from "@headlessui/react";
import type { ComponentPropsWithoutRef } from "react";
import { Fragment, useRef } from "react";

import "./styles.scss";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";

const invisibleCharacter = "&#8203;";

export interface FullScreenModalProps {
  children: React.ReactNode;
  className?: string;
  hideCloseButton?: boolean;
  hideTitle?: boolean;
  onClose: () => void;
  passThroughProps?: Partial<ComponentPropsWithoutRef<typeof Dialog>>;
  show: boolean;
  title?: string;
}

/** @deprecated Please use the new Modal components at "@circle-react-uikit/ModalV2" */
export const FullScreenModal = ({
  show,
  onClose,
  className,
  title,
  passThroughProps,
  hideCloseButton,
  hideTitle,
  children,
}: FullScreenModalProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  return (
    <Transition.Root
      show={show}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Dialog
        as="div"
        static
        className={classNames(legacyClassNames("fullscreen-modal"), className)}
        initialFocus={hideCloseButton ? modalBodyRef : cancelButtonRef}
        open={show}
        onClose={onClose}
        {...passThroughProps}
      >
        <div className={legacyClassNames("fullscreen-modal__body-outer")}>
          <Dialog.Overlay className="fixed inset-0 bg-gray-900/50" />
          <span
            className="hidden"
            aria-hidden="true"
            style={{ content: invisibleCharacter }}
          />
          <div className={legacyClassNames("fullscreen-modal__body-wrap")}>
            <div
              className={legacyClassNames("fullscreen-modal__body")}
              ref={modalBodyRef}
            >
              {!hideCloseButton && (
                <button
                  type="submit"
                  className={legacyClassNames("fullscreen-modal__close-icon")}
                  onClick={onClose}
                  ref={cancelButtonRef}
                >
                  <Icon type="close" />
                </button>
              )}

              {!hideTitle && (
                <h2 className={legacyClassNames("fullscreen-modal__title")}>
                  {title}
                </h2>
              )}
              {children}
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
